import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { useDraggable } from '@dnd-kit/core';
import { ChevronDown, ChevronRight, Lock, AlertCircle } from 'lucide-react';
import { tasksApi } from '@/lib/api';
import { useTaskStatusConfigs } from '@/hooks/useTaskStatusConfigs';
import { Avatar } from '@/components/ui/Avatar';
import { cn, formatDate } from '@/lib/utils';

interface StatusConfig {
  id: string;
  name: string;
  slug: string;
  color: string;
  position: number;
  isDefault: boolean;
  isClosed: boolean;
}

/**
 * How many finished cards a lane shows before it stops.
 *
 * A done column is unbounded by nature — one project here holds 256 closed
 * tasks — and a lane that renders all of them buries every open column below a
 * screen of history. The count above the column stays truthful; only the
 * rendering stops.
 */
const DONE_VISIBLE = 5;

function TaskCard({ task, dragging }: { task: any; dragging?: boolean }) {
  const overdue =
    task.status !== 'COMPLETED' && task.dueDate && new Date(task.dueDate) < new Date();
  const people = task.assignees?.length ? task.assignees : task.assignee ? [task.assignee] : [];

  return (
    <div
      className={cn(
        'rounded-lg border border-grey-200 bg-white p-3 shadow-sm transition-shadow',
        dragging ? 'rotate-2 shadow-elevated' : 'hover:shadow-card',
      )}
    >
      <p className="line-clamp-2 text-sm font-medium text-secondary">{task.title}</p>
      <div className="mt-2 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          {people.slice(0, 3).map((p: any) => (
            <Avatar key={p.id} src={p.avatarUrl} name={p.name || '?'} size="xs" />
          ))}
          {people.length === 0 && <span className="text-xs text-grey-400">Unassigned</span>}
        </div>
        {task.dueDate && (
          <span
            className={cn(
              'shrink-0 text-xs',
              overdue ? 'font-medium text-error' : 'text-grey-500',
            )}
          >
            {overdue && <AlertCircle className="mr-1 inline h-3 w-3" />}
            {formatDate(task.dueDate)}
          </span>
        )}
      </div>
    </div>
  );
}

function DraggableTask({
  task,
  onOpen,
  canDrag,
}: {
  task: any;
  onOpen: (task: any) => void;
  canDrag: boolean;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: task.id,
    data: { type: 'task', statusId: task.statusId },
    disabled: !canDrag,
  });

  return (
    <div
      ref={setNodeRef}
      {...(canDrag ? listeners : {})}
      {...attributes}
      onClick={() => onOpen(task)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(task);
        }
      }}
      className={cn('w-full text-left', canDrag && 'cursor-grab active:cursor-grabbing', isDragging && 'opacity-40')}
    >
      <TaskCard task={task} />
    </div>
  );
}

function Column({
  status,
  tasks,
  onOpen,
  canDrag,
}: {
  status: StatusConfig;
  tasks: any[];
  onOpen: (task: any) => void;
  canDrag: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: status.id, data: { type: 'status' } });
  const [showAll, setShowAll] = useState(false);
  // Only the DONE column is truncated; an open column that hid work would hide
  // the very thing the board exists to surface.
  const limited = status.isClosed && !showAll;
  const visible = limited ? tasks.slice(0, DONE_VISIBLE) : tasks;
  const hidden = tasks.length - visible.length;

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex w-64 shrink-0 flex-col rounded-lg p-2 transition-colors',
        isOver ? 'bg-accent/5 ring-2 ring-accent/30' : 'bg-grey-50',
      )}
    >
      <div className="mb-2 flex items-center gap-2 px-1">
        <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: status.color }} />
        <span className="truncate text-caption font-bold uppercase tracking-wider text-grey-500">
          {status.name}
        </span>
        <span className="ml-auto shrink-0 text-caption font-bold text-grey-400">{tasks.length}</span>
      </div>

      <div className="flex flex-col gap-2">
        {visible.map((task) => (
          <DraggableTask key={task.id} task={task} onOpen={onOpen} canDrag={canDrag} />
        ))}
        {hidden > 0 && (
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="rounded-lg py-2 text-xs text-grey-500 transition-colors hover:bg-grey-100 hover:text-secondary"
          >
            Show {hidden} more
          </button>
        )}
        {tasks.length === 0 && (
          <div className="rounded-lg border border-dashed border-grey-200 py-6 text-center text-xs text-grey-400">
            Nothing here
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Every project's tasks on one board: columns are STATUSES, lanes are projects.
 *
 * Status is what a board is for — "where is this stuck" — and the existing
 * per-project board answers a different question, with task LISTS as columns.
 * This one can exist because there is exactly one status set: `projectId` on
 * TaskStatusConfig is nullable, null meaning the global template, and no
 * project currently overrides it. If one ever does, its extra statuses arrive
 * as their own columns rather than breaking the board — which is why columns
 * are keyed by the config id and ordered by `position`.
 *
 * Lanes are projects because the cross-project question people actually ask is
 * "which project is stuck", and an ungrouped wall of every task answers nothing.
 * The assignee filter lives on the parent screen so it applies to both views.
 */
export function ConsolidatedBoard({
  tasks,
  onOpenTask,
  canDrag = true,
}: {
  tasks: any[];
  onOpenTask: (task: any) => void;
  canDrag?: boolean;
}) {
  const queryClient = useQueryClient();
  const { data: statusConfigs } = useTaskStatusConfigs();
  const [activeTask, setActiveTask] = useState<any>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const sensors = useSensors(
    // A small distance threshold so a click still opens the task instead of
    // starting a drag the moment the pointer moves a pixel.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );

  const columns = useMemo<StatusConfig[]>(
    () => [...(statusConfigs ?? [])].sort((a, b) => a.position - b.position),
    [statusConfigs],
  );
  const defaultStatusId = columns.find((c) => c.isDefault)?.id ?? columns[0]?.id;

  const lanes = useMemo(() => {
    interface Lane {
      id: string;
      name: string;
      isPrivate: boolean;
      tasks: any[];
    }
    const byProject = new Map<string, Lane>();
    for (const task of tasks) {
      const project = task.list?.project;
      if (!project) continue;
      const lane: Lane = byProject.get(project.id) ?? {
        id: project.id,
        name: project.name,
        isPrivate: !!project.isPrivate,
        tasks: [],
      };
      lane.tasks.push(task);
      byProject.set(project.id, lane);
    }
    // Busiest first — the lane with the most in flight is the one worth
    // looking at, and alphabetical would bury it.
    return [...byProject.values()].sort(
      (a, b) => b.tasks.length - a.tasks.length || a.name.localeCompare(b.name),
    );
  }, [tasks]);

  const moveMutation = useMutation({
    mutationFn: ({ taskId, statusId }: { taskId: string; statusId: string }) =>
      tasksApi.updateTask(taskId, { statusId }),
    onMutate: async ({ taskId, statusId }) => {
      await queryClient.cancelQueries({ queryKey: ['allTasks'] });
      const previous = queryClient.getQueryData<any[]>(['allTasks']);
      if (previous) {
        queryClient.setQueryData(
          ['allTasks'],
          previous.map((t) => (t.id === taskId ? { ...t, statusId } : t)),
        );
      }
      return { previous };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['allTasks'], ctx.previous);
    },
    onSettled: () => {
      // Moving to a closed status also flips `status` and `completedAt` server
      // side, so the whole row has to come back — an optimistic statusId alone
      // would leave the card looking open in the done column.
      queryClient.invalidateQueries({ queryKey: ['allTasks'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });

  const handleDragStart = (e: DragStartEvent) => {
    setActiveTask(tasks.find((t) => t.id === e.active.id) ?? null);
  };

  const handleDragEnd = (e: DragEndEvent) => {
    setActiveTask(null);
    const { active, over } = e;
    if (!over || over.data.current?.type !== 'status') return;
    const targetStatusId = over.id as string;
    const task = tasks.find((t) => t.id === active.id);
    if (!task) return;
    const currentStatusId = task.statusId ?? defaultStatusId;
    if (currentStatusId === targetStatusId) return;
    moveMutation.mutate({ taskId: task.id, statusId: targetStatusId });
  };

  if (columns.length === 0) {
    return <p className="py-12 text-center text-sm text-grey-500">No task statuses are configured yet.</p>;
  }

  if (lanes.length === 0) {
    return <p className="py-12 text-center text-sm text-grey-500">No tasks match these filters.</p>;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="space-y-6" data-testid="consolidated-board">
        {lanes.map((lane) => {
          const isCollapsed = collapsed[lane.id];
          return (
            <section key={lane.id} aria-labelledby={`lane-${lane.id}`}>
              <div className="mb-2 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setCollapsed((c) => ({ ...c, [lane.id]: !c[lane.id] }))}
                  aria-expanded={!isCollapsed}
                  className="flex items-center gap-1 rounded p-1 text-grey-500 transition-colors hover:bg-grey-100 hover:text-secondary"
                >
                  {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
                <h3 id={`lane-${lane.id}`} className="flex items-center gap-2 font-semibold text-secondary">
                  <Link to={`/projects/${lane.id}`} className="hover:text-primary">
                    {lane.name}
                  </Link>
                  {lane.isPrivate && <Lock className="h-3.5 w-3.5 text-grey-400" />}
                </h3>
                <span className="text-xs text-grey-500">
                  {lane.tasks.length} task{lane.tasks.length === 1 ? '' : 's'}
                </span>
              </div>

              {!isCollapsed && (
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {columns.map((status) => (
                    <Column
                      key={status.id}
                      status={status}
                      tasks={lane.tasks.filter(
                        (t) => (t.statusId ?? defaultStatusId) === status.id,
                      )}
                      onOpen={onOpenTask}
                      canDrag={canDrag}
                    />
                  ))}
                </div>
              )}
            </section>
          );
        })}
      </div>

      <DragOverlay>{activeTask && <TaskCard task={activeTask} dragging />}</DragOverlay>
    </DndContext>
  );
}
